import React, { useState } from 'react';
import { Alert, ProgressBar, Container, Row, Col, Button } from 'react-bootstrap';
import api from '../../services/api';

const ProgressBanner = ({ execution, onClose, onCancel }) => {
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancellationError, setCancellationError] = useState(null);
  
  if (!execution) return null;

  const handleCancel = async () => {
    setIsCancelling(true);
    setCancellationError(null);
    
    try {
      await api.cancelExecution(false); // override = false as requested
      if (onCancel) {
        onCancel();
      }
    } catch (error) {
      console.error('Error cancelling execution:', error);
      setCancellationError('Failed to cancel execution. Please try again.');
      setIsCancelling(false);
    }
  };

  // Calculate elapsed time
  const calculateElapsedTime = (startTime) => {
    if (!startTime) return '0s';
    
    const start = new Date(startTime+'Z');
    const now = new Date(Date.now());
    const diffMs = now - start;

    console.log('[ProgressBanner] Calculating elapsed time:', { start, now, diffMs });
    
    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Format module name for display
  const formatModuleName = (moduleName) => {
    if (!moduleName) return 'Unknown Module';
    
    return moduleName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const elapsedTime = calculateElapsedTime(execution.timestamp_start);
  const formattedModuleName = formatModuleName(execution.executed_module);
  const progress = execution.current_progress || 0;
  const currentStep = isCancelling ? 'Cancellation in progress...' : (execution.current_step || 'Processing...');

  return (
    <Alert 
      variant="info" 
      className="mb-0 rounded-0 border-0 border-bottom"
      style={{
        background: 'linear-gradient(90deg, #e3f2fd 0%, #bbdefb 100%)',
        borderBottom: '2px solid #2196f3 !important'
      }}
    >
      <Container>
        <Row className="align-items-center">
          <Col md={3}>
            <div className="d-flex align-items-center">
              <div 
                className="spinner-border spinner-border-sm me-2" 
                role="status"
                style={{ color: '#1976d2' }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <strong>{formattedModuleName}</strong>
            </div>
          </Col>
          
          <Col md={4}>
            <div className="mb-1">
              <small className="text-muted">Current Step:</small>
              <div style={{ fontSize: '0.9em' }}>{currentStep}</div>
            </div>
          </Col>
          
          <Col md={3}>
            <div className="mb-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Progress:</small>
                <small><strong>{progress}%</strong></small>
              </div>
              <ProgressBar 
                now={progress} 
                variant="primary"
                animated
                striped
                style={{ height: '8px' }}
              />
            </div>
          </Col>
          
          <Col md={2} className="text-end">
            <div className="d-flex flex-column align-items-end">
              <div className="mb-2">
                <small className="text-muted">Elapsed:</small>
                <div style={{ fontSize: '0.9em', fontWeight: '500' }}>
                  {elapsedTime}
                </div>
              </div>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleCancel}
                disabled={isCancelling}
                style={{ fontSize: '0.8em' }}
              >
                {isCancelling ? (
                  <>
                    <div
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                      style={{ width: '12px', height: '12px' }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    Cancelling...
                  </>
                ) : (
                  'Cancel'
                )}
              </Button>
              {cancellationError && (
                <small className="text-danger mt-1" style={{ fontSize: '0.7em' }}>
                  {cancellationError}
                </small>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </Alert>
  );
};

export default ProgressBanner;